// The pill switch behind every standing on/off answer: the Statue's queued-volley
// auto-pay, the BAM Exploder's round-end auto-destroy, the Telescopic Arm's
// capture, and Energized Bun-Bun's double-pickup rider.
//
// Why it isn't a plain checkbox: these controls REPORT a state ("capture is
// currently off"), and a bare tick beside the word "Off" reads as a BUTTON —
// "click to turn off" — i.e. exactly backwards. A track-and-knob is unambiguous
// at a glance, which a tick never is.
//
// It stays a real `<input type="checkbox">` (with `role="switch"`) inside a
// `<label>`, so the hit target, keyboard toggling and focus ring are the
// browser's; only the painting is ours, in `.switch-toggle` (styles.css).

import type { ReactNode } from 'react';

export function SwitchToggle({
  checked,
  onChange,
  children,
  title,
  label,
  disabled = false,
  className = '',
}: {
  checked: boolean;
  /** Called with the NEW state (never with the old one). */
  onChange: (checked: boolean) => void;
  /** What sits beside the switch. Omit for the bare state word, "On" / "Off". */
  children?: ReactNode;
  title?: string;
  /** Accessible name — required when `children` is only the state word, since
   * "On" alone names nothing. Ignored when `children` is descriptive. */
  label?: string;
  disabled?: boolean;
  /** Extra class on the `<label>`: context styling (font size, and the
   * `--switch-accent` colour the track and state word take when on). */
  className?: string;
}) {
  return (
    <label className={`switch-toggle${className ? ` ${className}` : ''}`} title={title}>
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        aria-label={label}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{children ?? (checked ? 'On' : 'Off')}</span>
    </label>
  );
}
